import { useState } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete, {
    AutocompleteProps as AutoCompletePropsMUI
} from '@mui/material/Autocomplete';
import type { ChipTypeMap } from '@mui/material';
import { compact, noop } from 'lodash';
import TextFieldStyled from './TextFieldStyled';

import useFormFieldStyles from './styles';

export interface AutoCompleteProps<
    T,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
    ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent']
> extends Omit<
        AutoCompletePropsMUI<T, Multiple, DisableClearable, FreeSolo, ChipComponent>,
        'name' | 'renderInput'
    > {
    label: string;
    name: string;
    value: any;
    // eslint-disable-next-line no-unused-vars
    setFieldValue?: (name: string, value: any) => void;
    // eslint-disable-next-line no-unused-vars
    setTouched?: (name: string, value: boolean) => void;
    options: any[];
    keysToLabel?: string[];
    disabled?: boolean;
    selectionDisabled?: boolean;
    // eslint-disable-next-line no-unused-vars
    customOnChange?: ((e: any, value: any) => void) | null;
    // eslint-disable-next-line no-unused-vars
    onInput?: (value: any) => void;
    onPasteInput?: React.ClipboardEventHandler<HTMLInputElement>;
    onOpen?: () => void;
    deleteBtn?: JSX.Element | null;
    touched?: boolean;
    errors?: string;
}

const AutoComplete = <
    T extends { name: string; disabled?: boolean; id: string },
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
    ChipComponent extends React.ElementType
>({
    label,
    name,
    value,
    setFieldValue,
    setTouched = noop,
    options,
    keysToLabel = [],
    disabled = false,
    selectionDisabled = false,
    customOnChange = null,
    onInput = () => {},
    onPasteInput,
    deleteBtn = null,
    touched,
    errors,
    ...rest
}: AutoCompleteProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent>) => {
    const classes = useFormFieldStyles({
        error: false
    });

    const [noOptionsText, setNoOptionsText] = useState('Введите запрос');

    const resolveField = (val: any): string =>
        val && typeof val === 'object' ? val.name ?? '' : val ?? '';

    const getOptionLabel = (option: any) =>
        option
            ? compact([option?.name, ...keysToLabel.map((key) => resolveField(option[key]))]).join(
                  ', '
              )
            : '';

    return (
        <Autocomplete
            // @ts-ignore
            name={name}
            options={options}
            getOptionLabel={getOptionLabel}
            getOptionDisabled={(option) => !!option.disabled}
            value={value || ''}
            classes={{
                root: classes.autocomplete,
                input: classes.input,
                inputFocused: classes.input,
                listbox: classes.input
            }}
            isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
            onChange={(e, value) => {
                if (!customOnChange) {
                    const newFieldValue = value || '';

                    setFieldValue?.(name, newFieldValue);
                } else {
                    customOnChange(e, value);
                }
            }}
            onInput={(e) => {
                setNoOptionsText(
                    (e.target as HTMLInputElement).value ? 'Ничего не найдено' : 'Введите запрос'
                );
                onInput(e);
            }}
            disabled={selectionDisabled}
            onBlur={() => setTouched && setTouched(name, true)}
            renderInput={({
                inputProps: nativeInputProps,
                InputProps: MuiInputProps,
                ...params
            }) => (
                <TextFieldStyled
                    {...params}
                    label={label}
                    variant="outlined"
                    FormHelperTextProps={{
                        className: classes.errorText
                    }}
                    InputProps={{
                        ...MuiInputProps,
                        inputProps: {
                            ...nativeInputProps,
                            ...(onPasteInput && { onPaste: onPasteInput })
                        },
                        endAdornment: disabled && (
                            <InputAdornment position="end">
                                <CircularProgress size={20} />
                            </InputAdornment>
                        ),
                        ...(deleteBtn && !disabled && { endAdornment: deleteBtn })
                    }}
                    error={!!(touched && errors)}
                    helperText={touched && errors}
                    disabled={selectionDisabled}
                    fullWidth
                />
            )}
            modifiers={{
                fixed: true
            }}
            openOnFocus
            noOptionsText={noOptionsText}
            {...rest}
        />
    );
};

export default AutoComplete;
